import {
  AVATAR_PHOTO_DPR,
  AVATAR_PHOTO_VARIANTS,
  displayNameOf,
  type AvatarPhotoVariant,
  type DisplayNameParts,
} from "@bing/shared";
import { API_URL } from "../config";
import { fa } from "../i18n/fa";

export type { DisplayNameParts } from "@bing/shared";

/**
 * The URL that serves an uploaded avatar photo as a JPEG (`GET /avatar/:id`).
 * Usable directly as an `<img src>` (the api serves it image-response-style), so
 * the shared `Avatar` can render a real photo instead of a preset when a user has
 * one. Preset avatars (no upload) keep using the emoji/gradient disc.
 *
 * With a `copy` size it is the smaller copy of the same photo (`GET /avatar/:id/:size`, a
 * `copy`×`copy` JPEG made from the original) — what a spot that draws the avatar small asks for.
 * Without one it is the full image, byte for byte the URL every older bundle requests.
 */
export function avatarPhotoUrl(avatarPhotoId: string, copy?: AvatarPhotoVariant | null): string {
  const original = `${API_URL}/avatar/${encodeURIComponent(avatarPhotoId)}`;
  return copy ? `${original}/${copy}` : original;
}

/**
 * The copy for a photo drawn `cssSize` CSS px wide on THIS screen — sized for the screen's own
 * density, never below the DPR {@link AVATAR_PHOTO_DPR} the copies are built for. At or below DPR 3
 * it is exactly `avatarPhotoVariantFor` (the copy the quality probe measured); a denser screen
 * (3.5, 4 — real Android phones) moves up a copy, or to the full image (`null`), instead of
 * stretching one. A missing, NaN, zero, negative or infinite ratio counts as 3.
 */
export function avatarPhotoCopyFor(
  cssSize: number,
  devicePixelRatio?: number,
): AvatarPhotoVariant | null {
  // `Number.isFinite` never coerces, so undefined / NaN / ±Infinity all land on 0 → the DPR-3 floor.
  const ratio = Number.isFinite(devicePixelRatio) ? Number(devicePixelRatio) : 0;
  const devicePx = Math.ceil(cssSize * Math.max(AVATAR_PHOTO_DPR, ratio));
  return AVATAR_PHOTO_VARIANTS.find((side) => side >= devicePx) ?? null;
}

/**
 * The URL that serves a picture attached to an in-app «@bing» broadcast
 * (`GET /inbox-image/:id`) — a self-hosted JPEG, usable directly as an `<img src>`
 * in the Messages screen. Self-hosted (not a Telegram file link) so it loads
 * reliably on the app's own domain even where `api.telegram.org` is unstable.
 */
export function inboxImageUrl(imageId: string): string {
  return `${API_URL}/inbox-image/${encodeURIComponent(imageId)}`;
}

/** The fields any display-name resolution needs (a structural subset of `Profile`). */
export interface NameableProfile {
  nickname?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  appUsername?: string | null;
}

/**
 * Best display name for the signed-in user — the SAME shared priority as every
 * other surface (nickname → Telegram first/last name → app handle without `@`),
 * with the Profile title as the neutral fallback. The Telegram `@username` is
 * deliberately NOT part of the chain: identifiers never render as names.
 * Shared by the Profile screen hero and the lobby header chip.
 */
export function profileDisplayName(profile: NameableProfile | null | undefined): string {
  if (!profile) return fa.profile.title;
  return displayNameOf(profile) ?? fa.profile.title;
}

/**
 * The one place another user's name is resolved — reused by every surface that
 * shows someone else (friends list, requests, user search, chat header/rows,
 * invite prompts), so a user is labelled identically everywhere. Delegates to
 * the shared `displayNameOf` priority the api's notifications use too:
 *   1. `nickname` — the in-game display name;
 *   2. the Telegram name `firstName [lastName]`;
 *   3. the bare `appUsername` (no `@`) — last resort before the placeholder,
 *      so a handle-only user still reads as themselves rather than "(کاربر)";
 *   4. a neutral "(کاربر)" only when there is truly no name data.
 * NEVER a Telegram `@username` (other-user views don't even carry it) and never
 * a numeric id.
 */
export function displayName(u: DisplayNameParts): string {
  return displayNameOf(u) ?? fa.friends.user;
}

/**
 * Cap a resolved player name to `max` code points (appending «…») so a long name can't
 * blow out a fixed table box or push an adjacent notification onto two lines. Use this
 * where a name is embedded in a SENTENCE (turn / hakem / player-left / win notices), so
 * only the name shortens while the surrounding words stay visible. Bare seat labels use
 * CSS `max-width` + `truncate` instead. Counts by code point (emoji-safe).
 */
export function shortName(name: string, max = 16): string {
  const chars = [...name];
  return chars.length > max ? `${chars.slice(0, max).join("")}…` : name;
}

/**
 * The `@appUsername` subline — rendered ONLY where users search by handle (the
 * Add-friend tab), and only when it adds information the name line doesn't
 * already carry (i.e. the display name is a real name, not the handle itself).
 * Returns undefined when there's no handle, or when the handle IS the display
 * name (a handle-only user).
 */
export function userHandle(u: DisplayNameParts): string | undefined {
  const handle = u.appUsername?.trim();
  if (!handle) return undefined;
  return displayName(u) === handle ? undefined : `@${handle}`;
}
